const COMPONENT_META = Symbol.for("bitecs.componentMeta");

export type ComponentScope = 
  | "system"
  | "render";

export type EditorControlType =
  | "number"
  | "slider"
  | "colour"
  | "enum"
  | "time"
  | "entity"
  | "angle"
  | "checkbox";

type EnumOption<T extends number | string = number | string> = {
  label: string;
  value: T;
};

type PropertyMeta = {
  // User-readable name of property
  label?: string;

  // User-readable description of property
  description?: string;

  // Which control to use in the editor to represent this property
  control?: EditorControlType;

  // Applicable if control is numeric
  step?: number; // How much to increase/decrease the number by
  min?: number; // Minimum amount
  max?: number; // Maximum amount
  precision?: number; // Number of decimal places to render

  // Applicable if the control is an enum
  enumOptions?: ReadonlyArray<EnumOption>; // User-readable options (with label and value) to render in editor

  // General options
  readOnly?: boolean; // Whether this can be modified
  hidden?: boolean; // Whether this renders in the editor
};

type ComponentMeta = {
  // User readable name of component
  name: string;

  // Identical to the actual name of the component in the code. Defaults to name, set if different than name
  componentName?: string;

  // Scope: renderable only matters in FE, system is necessary for calculating actual game state
  scope?: ComponentScope;

  // User readable description of component
  description?: string;

  // Props of this component
  props?: Record<string, PropertyMeta>;
};

type RegisteredComponent = object;

const componentsByName = new Map<string, RegisteredComponent>();
const metaByComponent = new WeakMap<object, ComponentMeta>();

// Run against a new component to give it information relevant for the editor
// (and some non-comment based documentation)
export function defineComponentMeta<T extends object>(
  component: T,
  meta: ComponentMeta
): T {
  Object.defineProperty(component, COMPONENT_META, {
    value: meta,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  
  const componentDefaults: Partial<ComponentMeta> = {
    componentName: meta.name,
    scope: "system",
  }

  const compMetaWithDefaults = {
    ...componentDefaults,
    ...meta,
  }

  const existing = componentsByName.get(meta.name);
  if (existing && existing !== component) {
    throw new Error(`A component named "${meta.name}" is already registered.`);
  }

  metaByComponent.set(component, compMetaWithDefaults);

  componentsByName.set(meta.name, component);

  return component;
}

// Return metadata from a particular component that had metadata defined
export function getComponentMeta(component: object): ComponentMeta | undefined {
  return (component as any)[COMPONENT_META];
}

// Get just the name of a particular component
export function getComponentName(component: object): string | undefined {
  return getComponentMeta(component)?.name;
}

// Get property metadata for a single property
export function getPropertyMeta(
  component: object,
  prop: string
): PropertyMeta | undefined {
  return getComponentMeta(component)?.props?.[prop];
}

// Get component object by the name by which it was defined
// Note: this refers to the `name` property and not `componentName`
export function getComponentFromName<T extends object = object>(
  name: string
): T | undefined {
  return componentsByName.get(name) as T | undefined;
}

type EnumLike = Record<string, string | number>;
// Utility function to transform an enum in CAPITAL_SNAKE_CASE to user-readable options
const formatEnum = (str: string) => str
  .split('_')
  .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
  .join(' ')

// Quickly transform an enum into user-readable options
export function enumToOptions<E extends EnumLike>(
  e: E
): ReadonlyArray<{ label: string; value: E[keyof E] }> {
  return Object.keys(e)
    .filter((k) => Number.isNaN(Number(k)))
    .map((k) => ({
      label: formatEnum(k),
      value: e[k] as E[keyof E],
    }));
}